import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { allBlogArticles } from "@/data/blogArticles";
import { ArrowRight } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import { prefetchOn } from "@/lib/prefetchRoute";
import PictureImg from "@/components/ui/PictureImg";

const BlogPreview = forwardRef<HTMLElement>((_, ref) => {
  const latest = allBlogArticles.slice(0, 3);

  return (
    <section ref={ref} className="bg-background py-24 sm:py-32 border-t border-border">
      <div className="container mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
              <div>
                <div className="text-xs font-medium text-primary tracking-[0.22em] uppercase mb-3">
                  From the journal
                </div>
                <h2 className="font-display text-4xl sm:text-5xl text-foreground tracking-tight">
                  Notes from the workshop.
                </h2>
              </div>
              <Link
                to="/blog"
                {...prefetchOn("/blog")}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors link-underline"
              >
                All articles <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid sm:grid-cols-3 gap-8">
            {latest.map((article, i) => (
              <ScrollReveal key={article.slug} delay={i * 100}>
                <Link to={`/blog/${article.slug}`} {...prefetchOn(`/blog/${article.slug}`)} className="group block">
                  <div className="aspect-[4/3] overflow-hidden bg-muted mb-5 rounded-lg border border-border">
                    <PictureImg
                      src={article.heroImage}
                      alt={article.heroAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      width={400}
                      height={300}
                      sizes="(min-width: 640px) 33vw, 100vw"
                    />


                  </div>
                  <div className="text-xs tracking-[0.18em] uppercase text-primary/70 mb-2">
                    {article.category}
                  </div>
                  <h3 className="font-display text-xl text-foreground leading-snug mb-2 group-hover:text-primary transition-colors">
                    {article.title}
                  </h3>
                  <div className="text-xs text-muted-foreground">
                    {new Date(article.publishDate).toLocaleDateString("en-US", { month: "long", day: "numeric" })} · {article.readTime}
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
});

BlogPreview.displayName = "BlogPreview";

export default BlogPreview;
